/* Rows in the findings list for checks that produced no findings because they
 * did not run.
 *
 * The reconciliation check has its own row builder next door, because it is the
 * one check a reviewer can do something about from here: it is waiting on two
 * answers. Every other check that did not run is reported the same plain way —
 * what it is, that it did not run, why, and what it was looking for when it
 * gave up.
 *
 * The rule this exists to hold: a check that could not run must never look like
 * a check that ran and found nothing. An empty findings list with no
 * explanation says "your plan is clean". These rows are the difference.
 *
 * Pure functions, so tools/new-review-check.tsx can assert them without a
 * browser.
 */

import { CHECK_LABELS } from '../../data/report';
import {
  PENDING_ID_PREFIX,
  RECONCILE_CHECK_ID,
  type PendingCheckRow,
} from './reconcileModel';
import type { CheckFailure, CheckSkip } from '../../types';

/**
 * What each check is looking for, said as a question about the plan rather than
 * as the name of a routine. The rail row is a claim the reviewer can read; the
 * check id is provenance and never appears.
 */
export const CHECK_CLAIMS: Record<string, string> = {
  [RECONCILE_CHECK_ID]: 'Whether two tables report different savings for the same measure',
  'citation-check': 'Whether every external reference names a version and a date',
  'source-attribution-check': 'Whether every estimated figure says where it came from',
  'generation-check': 'Whether the governing contract required the baseline to be witnessed',
  'completeness-check': 'Whether anything on FEMP’s required-contents list is missing',
  'model-classification': 'The eleven checks that read clause language rather than arithmetic',
};

/** A check with no plain claim written for it still gets a readable row. */
export function claimFor(checkId: string): string {
  if (CHECK_CLAIMS[checkId]) return CHECK_CLAIMS[checkId];
  const label = CHECK_LABELS[checkId];
  if (label) return label.split('—')[0].trim();
  return 'A check that did not run';
}

export interface SkippedRowOptions {
  /**
   * Check ids another row already speaks for. The reconciliation check has its
   * own row with the questions in it, so its skip must not be reported twice.
   */
  handled?: string[];
}

/**
 * One row per check that did not run, and one per check that stopped part way.
 * All of them sit in "Couldn't check", beneath the plan areas.
 */
export function skippedCheckRows(
  skipped: CheckSkip[],
  failures: CheckFailure[] = [],
  options: SkippedRowOptions = {},
): PendingCheckRow[] {
  const handled = new Set(options.handled ?? [RECONCILE_CHECK_ID]);
  const rows: PendingCheckRow[] = [];

  for (const skip of skipped) {
    if (handled.has(skip.checkId)) continue;
    handled.add(skip.checkId);
    rows.push({
      id: `${PENDING_ID_PREFIX}${skip.checkId}`,
      checkId: skip.checkId,
      state: 'unavailable',
      group: 'couldnt-check',
      claim: claimFor(skip.checkId),
      contextLine: 'This check did not run against this document.',
      reason: skip.reason,
      lookedFor: skip.lookedFor,
      mark: 'Not checked',
    });
  }

  for (const failure of failures) {
    if (handled.has(failure.checkId)) continue;
    handled.add(failure.checkId);
    rows.push({
      id: `${PENDING_ID_PREFIX}${failure.checkId}`,
      checkId: failure.checkId,
      state: 'unavailable',
      group: 'couldnt-check',
      claim: claimFor(failure.checkId),
      contextLine: 'This check stopped before it finished.',
      reason: failure.message,
      mark: 'Not checked',
    });
  }

  return rows;
}
